import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  LinearScale,
  Tooltip,
  type ChartData,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { ChevronDown, Loader2, Send, TrendingDown, TrendingUp, X } from 'lucide-react';
import { useBusiness } from '../../contexts/BusinessContext';
import { formatCurrency } from '../../lib/utils';
import {
  CASH_PAYMENT_TYPES,
  useCashCollectedDetails,
  type CashDayRow,
} from '../../hooks/useCashCollectedDetails';
import type { AccountsReceivableSummary } from '../../lib/invoices/fetchAccountsReceivable';
import SendStatementModal from '../SendStatementModal';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip);

interface CashCollectedMonthModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** A/R aging already loaded by the hero banner — for the past-due strip. */
  arSummary: AccountsReceivableSummary | null;
}

const AGING_CHIP_STYLES: Record<string, string> = {
  '0_30': 'text-amber-700',
  '31_60': 'text-orange-700',
  '61_plus': 'text-red-700',
};

const AGING_CHIP_LABELS: Record<string, string> = {
  '0_30': '0–30d',
  '31_60': '31–60d',
  '61_plus': '61+d',
};

function trendPct(current: number, previous: number): number | null {
  if (!Number.isFinite(previous) || previous === 0) return null;
  return ((current - previous) / previous) * 100;
}

function TrendChip({ pct }: { pct: number | null }) {
  if (pct === null) return null;
  const up = pct >= 0;
  const Icon = up ? TrendingUp : TrendingDown;
  return (
    <span
      className={`inline-flex items-center gap-1 text-[12px] font-semibold tabular-nums ${
        up ? 'text-green-700' : 'text-red-700'
      }`}
    >
      <Icon className="h-3.5 w-3.5" />
      {Math.abs(pct).toFixed(0)}%
    </span>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
      {children}
    </h3>
  );
}

/** Payment types that actually have money this month, in widget display order. */
function activePaymentTypes(days: CashDayRow[]) {
  return CASH_PAYMENT_TYPES.filter(({ type }) =>
    days.some((d) => (d.amounts[type] ?? 0) > 0)
  );
}

function DailyCollectionsChart({ days }: { days: CashDayRow[] }) {
  const labels = days.map((d) => d.label);
  const chartData: ChartData<'bar', number[], string> = {
    labels,
    datasets: activePaymentTypes(days).map(({ type, color }) => ({
      label: type,
      data: days.map((d) => d.amounts[type] ?? 0),
      backgroundColor: color,
      borderRadius: 3,
      maxBarThickness: 16,
    })),
  };

  return (
    <div className="h-[200px] w-full">
      <Bar
        data={chartData}
        options={{
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            tooltip: {
              callbacks: {
                title: (items) => {
                  const day = days[items[0]?.dataIndex ?? -1];
                  return day ? `${day.label} — ${formatCurrency(day.total)}` : '';
                },
                label: (ctx) => `${ctx.dataset.label}: ${formatCurrency(ctx.raw as number)}`,
              },
            },
          },
          scales: {
            x: {
              stacked: true,
              grid: { display: false },
              ticks: { maxRotation: 0, autoSkip: true, maxTicksLimit: 12, font: { size: 10 } },
            },
            y: {
              stacked: true,
              grid: { color: 'rgba(0,0,0,0.06)' },
              ticks: { font: { size: 10 } },
            },
          },
        }}
      />
    </div>
  );
}

function StatBlock({
  label,
  value,
  valueClassName,
}: {
  label: string;
  value: string;
  valueClassName?: string;
}) {
  return (
    <div className="min-w-0">
      <p className="text-[11px] text-gray-500">{label}</p>
      <p
        className={`text-sm font-semibold tabular-nums tracking-tight ${
          valueClassName ?? 'text-gray-900'
        }`}
      >
        {value}
      </p>
    </div>
  );
}

/** Deep-dive into this month's collections: trend, chart, channels, composition, A/R, feed. */
export default function CashCollectedMonthModal({
  isOpen,
  onClose,
  arSummary,
}: CashCollectedMonthModalProps) {
  const { selectedBusiness } = useBusiness();
  const { summary, days, sources, feedDays, monthLabel, loading, error, load } =
    useCashCollectedDetails();
  const [feedExpanded, setFeedExpanded] = useState(false);
  const [statementOpen, setStatementOpen] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setFeedExpanded(false);
      void load();
    }
  }, [isOpen, load]);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      // The statement modal sits on top and owns Escape while it's open.
      if (e.key === 'Escape' && !statementOpen) onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose, statementOpen]);

  if (!isOpen) return null;

  const pct = trendPct(summary.cashCollected, summary.lastMonthSamePoint);

  return createPortal(
    <div
      className="fixed inset-0 z-[99998] flex items-center justify-center p-3 sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cash-collected-month-title"
    >
      <div
        className="absolute inset-0 bg-black/30 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />
      <div
        className="relative w-full max-w-3xl max-h-[min(92vh,760px)] flex flex-col rounded-2xl border border-gray-200/90 bg-white shadow-[0_20px_60px_rgba(0,0,0,0.18)] overflow-hidden"
        style={{
          fontFamily:
            "-apple-system, BlinkMacSystemFont, 'SF Pro Text', system-ui, sans-serif",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 px-4 pt-4 pb-3 border-b border-gray-100 shrink-0">
          <div className="min-w-0">
            <h2
              id="cash-collected-month-title"
              className="text-base font-semibold text-gray-900"
            >
              Cash collected
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">{monthLabel || 'Month to date'}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Headline strip */}
        <div className="px-4 py-3 bg-gray-50/80 border-b border-gray-100 shrink-0">
          {loading ? (
            <Loader2 className="h-6 w-6 animate-spin text-gray-300" />
          ) : (
            <>
            <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-2">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                  Cash collected this month
                </p>
                <div className="flex items-baseline gap-2 mt-0.5">
                  <p className="text-2xl font-bold text-gray-900 tabular-nums tracking-tight">
                    {formatCurrency(summary.cashCollected)}
                  </p>
                  <TrendChip pct={pct} />
                </div>
                {summary.creditRedeemed > 0 ? (
                  <p className="text-[11px] font-medium text-teal-700 mt-0.5 tabular-nums">
                    + {formatCurrency(summary.creditRedeemed)} stored credit redeemed
                  </p>
                ) : null}
                {pct !== null ? (
                  <p className="text-[11px] text-gray-500 mt-0.5">
                    vs {formatCurrency(summary.lastMonthSamePoint)} at this point last month
                  </p>
                ) : null}
              </div>
              <div className="flex items-start gap-5 sm:gap-7">
                <StatBlock label="Today" value={formatCurrency(summary.collectedToday)} />
                <StatBlock label="Avg per day" value={formatCurrency(summary.avgPerDay)} />
                <StatBlock
                  label="Projected month-end"
                  value={formatCurrency(summary.projectedMonthEnd)}
                />
              </div>
            </div>

            {/* Past-due aging at a glance + statement action */}
            {arSummary ? (
              <div className="mt-2.5 pt-2.5 border-t border-gray-200/70 flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
                  <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                    Past due
                  </span>
                  {arSummary.buckets.map((bucket) => (
                    <span
                      key={bucket.key}
                      className="inline-flex items-baseline gap-1.5 rounded-lg border border-gray-200 bg-white px-2 py-1"
                    >
                      <span className="text-[10px] font-medium text-gray-500">
                        {AGING_CHIP_LABELS[bucket.key] ?? bucket.label}
                      </span>
                      <span
                        className={`text-[12px] font-semibold tabular-nums ${
                          bucket.totalBalance > 0
                            ? AGING_CHIP_STYLES[bucket.key] ?? 'text-gray-900'
                            : 'text-gray-400'
                        }`}
                      >
                        {formatCurrency(bucket.totalBalance)}
                      </span>
                      {bucket.invoiceCount > 0 ? (
                        <span className="text-[10px] text-gray-400 tabular-nums">
                          {bucket.invoiceCount}
                        </span>
                      ) : null}
                    </span>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => setStatementOpen(true)}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-[12px] font-semibold text-gray-700 hover:bg-gray-50 hover:text-gray-900 transition-colors"
                >
                  <Send className="h-3.5 w-3.5" />
                  Statement
                </button>
              </div>
            ) : null}
            </>
          )}
        </div>

        {/* Scroll body */}
        <div className="flex-1 min-h-0 overflow-y-auto ios-momentum-scroll px-4 py-4 space-y-5">
          {loading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="h-7 w-7 animate-spin text-gray-300" />
            </div>
          ) : error ? (
            <p className="text-sm text-red-600 text-center py-10">{error}</p>
          ) : (
            <>
              {/* Daily collections chart */}
              <section>
                <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
                  <SectionTitle>Daily collections</SectionTitle>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-gray-500">
                    {activePaymentTypes(days).map(({ type, color }) => (
                      <span key={type} className="inline-flex items-center gap-1.5">
                        <span
                          className="h-2 w-2 rounded-full"
                          style={{ backgroundColor: color }}
                        />
                        {type}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="mt-2 rounded-xl border border-gray-200 bg-white p-3">
                  {days.length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-8">
                      No payments recorded this month.
                    </p>
                  ) : (
                    <DailyCollectionsChart days={days} />
                  )}
                </div>
              </section>

              {/* Source + method breakdown: invoice collections vs stored-credit sales */}
              <section>
                <SectionTitle>Where it came from</SectionTitle>
                {sources.length === 0 ? (
                  <div className="mt-2 rounded-xl border border-gray-200 bg-white">
                    <p className="text-sm text-gray-500 text-center py-6">No payments yet.</p>
                  </div>
                ) : (
                  <div className="mt-2 space-y-3">
                    {sources.map((source) => (
                      <div
                        key={source.key}
                        className="rounded-xl border border-gray-200 bg-white overflow-hidden"
                      >
                        <div
                          className={`flex items-baseline justify-between gap-3 px-3 py-2 border-b border-gray-100 ${
                            source.key === 'credit_redeemed' ? 'bg-teal-50/70' : 'bg-gray-50/80'
                          }`}
                        >
                          <span className="text-[12px] font-semibold text-gray-700">
                            {source.label}
                            <span className="ml-1.5 text-[11px] font-normal text-gray-400">
                              {source.count} {source.count === 1 ? 'payment' : 'payments'}
                            </span>
                            {source.key === 'credit_redeemed' ? (
                              <span className="ml-1.5 text-[10px] font-medium text-teal-700">
                                Not new cash — counted when the credit was purchased
                              </span>
                            ) : null}
                          </span>
                          <span
                            className={`text-[13px] font-bold tabular-nums shrink-0 ${
                              source.key === 'credit_redeemed' ? 'text-teal-700' : 'text-gray-900'
                            }`}
                          >
                            {formatCurrency(source.total)}
                          </span>
                        </div>
                        <div className="divide-y divide-gray-100">
                          {source.channels.map((ch) => (
                            <div key={ch.key} className="px-3 py-2.5">
                              <div className="flex items-baseline justify-between gap-3">
                                <span className="inline-flex items-baseline gap-1.5 text-sm font-medium text-gray-800">
                                  <span
                                    className="h-2 w-2 self-center rounded-full shrink-0"
                                    style={{ backgroundColor: ch.color }}
                                  />
                                  {ch.label}
                                  <span className="text-[11px] font-normal text-gray-400">
                                    {ch.count} {ch.count === 1 ? 'payment' : 'payments'}
                                  </span>
                                </span>
                                <span className="text-sm font-semibold text-gray-900 tabular-nums shrink-0">
                                  {formatCurrency(ch.amount)}
                                  <span className="ml-1.5 text-[11px] font-normal text-gray-400">
                                    {(ch.share * 100).toFixed(0)}%
                                  </span>
                                </span>
                              </div>
                              <div className="mt-1.5 h-1.5 rounded-full bg-gray-100 overflow-hidden">
                                <div
                                  className="h-full rounded-full"
                                  style={{
                                    width: `${Math.max(ch.share * 100, 1.5)}%`,
                                    backgroundColor: ch.color,
                                  }}
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </section>

              {/* Composition of the headline number */}
              <section>
                <SectionTitle>Inside the number</SectionTitle>
                  <div className="mt-2 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm">
                    {(
                      [
                        ['Principal (pays invoices)', summary.principal, 'text-gray-900'],
                        ['Tips', summary.tips, 'text-gray-900'],
                        ['Card surcharges', summary.surcharges, 'text-gray-900'],
                      ] as const
                    ).map(([label, value, cls]) => (
                      <div
                        key={label}
                        className="flex items-baseline justify-between gap-3 py-1.5"
                      >
                        <span className="text-gray-600">{label}</span>
                        <span className={`font-semibold tabular-nums ${cls}`}>
                          {formatCurrency(value)}
                        </span>
                      </div>
                    ))}
                    <div className="flex items-baseline justify-between gap-3 py-1.5 border-t border-gray-100">
                      <span className="text-gray-600">Cash collected</span>
                      <span className="font-semibold text-gray-900 tabular-nums">
                        {formatCurrency(summary.cashCollected)}
                      </span>
                    </div>
                    {summary.creditRedeemed > 0 ? (
                      <div className="flex items-baseline justify-between gap-3 py-1.5">
                        <span className="text-gray-600">Stored credit redeemed</span>
                        <span className="font-semibold text-teal-700 tabular-nums">
                          + {formatCurrency(summary.creditRedeemed)}
                        </span>
                      </div>
                    ) : null}
                    <div className="flex items-baseline justify-between gap-3 py-1.5">
                      <span className="text-gray-600">Stripe processing fees</span>
                      <span className="font-semibold text-red-700 tabular-nums">
                        {summary.stripeFees > 0
                          ? `−${formatCurrency(summary.stripeFees)}`
                          : formatCurrency(0)}
                      </span>
                    </div>
                    <div className="flex items-baseline justify-between gap-3 py-1.5 border-t border-gray-100">
                      <span className="font-medium text-gray-800">Net after fees</span>
                      <span className="font-bold text-gray-900 tabular-nums">
                        {formatCurrency(summary.netAfterFees)}
                      </span>
                    </div>
                    <div className="flex items-baseline justify-between gap-3 py-1.5 border-t border-gray-100">
                      <span className="text-gray-600">
                        Refunds
                        {summary.refundsCount > 0 ? ` (${summary.refundsCount})` : ''}
                        {summary.voidsCount > 0 ? ` · ${summary.voidsCount} voided` : ''}
                      </span>
                      <span
                        className={`font-semibold tabular-nums ${
                          summary.refundsTotal > 0 ? 'text-red-700' : 'text-gray-900'
                        }`}
                      >
                        {summary.refundsTotal > 0
                          ? `−${formatCurrency(summary.refundsTotal)}`
                          : formatCurrency(0)}
                      </span>
                    </div>
                  </div>
                </section>

              {/* Recent payments feed — collapsed by default; rows render on expand */}
              <section>
                <div className="mt-2 rounded-xl border border-gray-200 bg-white overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setFeedExpanded((v) => !v)}
                    className="flex w-full items-center justify-between gap-3 px-3 py-2.5 hover:bg-gray-50 transition-colors"
                    aria-expanded={feedExpanded}
                  >
                    <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                      Payments this month
                      {summary.txCount > 0 ? ` · ${summary.txCount}` : ''}
                    </span>
                    <ChevronDown
                      className={`h-4 w-4 text-gray-400 transition-transform ${
                        feedExpanded ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                  {!feedExpanded ? null : feedDays.length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-6 border-t border-gray-100">
                      No payments recorded this month.
                    </p>
                  ) : (
                    <div className="border-t border-gray-100">
                    {feedDays.map((day) => (
                      <div key={day.date}>
                        <div
                          className={`px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide border-b border-gray-100 ${
                            day.isToday
                              ? 'bg-blue-50/80 text-blue-700'
                              : 'bg-gray-50/80 text-gray-500'
                          }`}
                        >
                          {day.label}
                          {day.isToday ? ' · Today' : ''}
                        </div>
                        <ul className="divide-y divide-gray-50">
                          {day.rows.map((row) => (
                            <li
                              key={row.id}
                              className="flex items-center justify-between gap-3 px-3 py-2"
                            >
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-gray-800 truncate">
                                  {row.customerName}
                                </p>
                                <p className="text-[11px] text-gray-500">
                                  {row.channelLabel} · {row.time}
                                  {row.refunded ? (
                                    <span className="ml-1.5 font-semibold text-red-600">
                                      Refunded
                                    </span>
                                  ) : null}
                                </p>
                              </div>
                              <div className="text-right shrink-0">
                                <p
                                  className={`text-sm font-semibold tabular-nums ${
                                    row.refunded
                                      ? 'text-red-600 line-through'
                                      : 'text-gray-900'
                                  }`}
                                >
                                  {formatCurrency(row.amount)}
                                </p>
                                {row.tip > 0 ? (
                                  <p className="text-[11px] text-gray-400 tabular-nums">
                                    incl. {formatCurrency(row.tip)} tip
                                  </p>
                                ) : null}
                              </div>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                    </div>
                  )}
                </div>
              </section>
            </>
          )}
        </div>
      </div>

      {selectedBusiness?.id ? (
        <SendStatementModal
          isOpen={statementOpen}
          onClose={() => setStatementOpen(false)}
          businessId={selectedBusiness.id}
          businessName={selectedBusiness.name ?? ''}
        />
      ) : null}
    </div>,
    document.body
  );
}
